import React, { useState } from "react";
import { motion, useSpring } from "framer-motion";
import useMediaQuery from "../hooks/useMediaQuery";

// Gentle trailing follow: the badge eases toward the pointer and settles.
const badgeSpring = { stiffness: 240, damping: 24, mass: 0.55 };

const ProjectItem = ({ project }) => {
  const aboveMediumScreens = useMediaQuery("(min-width: 800px)");
  const [isHover, setHover] = useState(false);

  // Badge position lives in motion values so pointer tracking never re-renders.
  const badgeX = useSpring(0, badgeSpring);
  const badgeY = useSpring(0, badgeSpring);

  // Handle links
  const handleClick = () => {
    window.location.href = project.url;
  };

  const handleMouseMove = (e) => {
    badgeX.set(e.clientX);
    badgeY.set(e.clientY);
  };

  // Scrolling can drag the card under a stationary pointer, which fires
  // mouseenter with no preceding mousemove. Seed the position from the enter
  // event itself so the badge appears already under the pointer rather than at
  // a stale spot - or, with the spring, gliding in from one.
  // `set` retargets the spring (so a still-scheduled restart can't drag the
  // badge back toward the old point), `jump` then places it there with no travel.
  const handleMouseEnter = (e) => {
    badgeX.set(e.clientX);
    badgeY.set(e.clientY);
    badgeX.jump(e.clientX);
    badgeY.jump(e.clientY);
    setHover(true);
  };

  return (
    <>
      {aboveMediumScreens ? (
        /* DESKTOP */
        <motion.div className="relative flex items-center w-[140%] lg:pt-20">
          {/* Project Details */}
          <div className="z-10 absolute">
            <p
              className="text-4xl lg:text-8xl lg:-ml-[20%] lg:absolute font-bold
              text-off-white md:absolute md:ml-11 font-satoshi"
            >
              {project.title}
            </p>
          </div>

          <motion.div
            data-testid="project-card"
            className="relative flex justify-center items-center lg:ml-[35%] ml-[32%] h-[100%]"
            style={{
              width: "50vw",
              maxWidth: "900px",
              maxHeight: "900px",
            }}
            whileHover={{ cursor: "pointer" }}
            onClick={handleClick}
            onMouseMove={handleMouseMove}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={() => setHover(false)}
          >
            {/* Background Image */}
            <img
              src={project.img}
              alt={project.title}
              className="object-cover w-full h-full z-0 rounded-lg"
            />
            {/* Video */}
            <div className="absolute inset-0 flex justify-center items-center">
              <video
                loop
                autoPlay
                muted
                className="shadow-lg z-10 w-[56%] h-[86%] pointer-events-none bg-sky-49"
                width="210"
                src={project.video}
              >
                Your browser does not support this video.
              </video>
            </div>
          </motion.div>

          {/* View */}
          <motion.div
            data-testid="view-badge-tracker"
            className="fixed top-0 left-0 z-50 pointer-events-none"
            style={{ x: badgeX, y: badgeY }}
          >
            {/* Centres the badge on the pointer without hard-coded half-sizes.
                Kept on its own node because framer-motion owns the inline
                transform of any element it animates scale on. */}
            <div className="-translate-x-1/2 -translate-y-1/2">
              <motion.div
                className="flex font-satoshi bg-sky-900 rounded-full
                w-[110px] h-[70px] justify-center items-center text-3xl"
                // Explicit, so the badge is hidden on the very first paint
                // rather than flashing at the viewport corner on page load.
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{
                  opacity: isHover ? 1 : 0,
                  scale: isHover ? 1 : 0.5,
                }}
                transition={{
                  scale: {
                    type: "spring",
                    stiffness: 300,
                    damping: 30,
                  },
                  opacity: {
                    duration: 0.3,
                    ease: "easeInOut",
                  },
                }}
              >
                View
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      ) : (
        <>
          {/* MOBILE */}
          <div onClick={handleClick}>
            <motion.div
              whileHover={{ cursor: "pointer" }}
              className="relative flex justify-center items-center"
            >
              {/* Background Image */}
              <div className="relative flex justify-center items-center h-[400px] w-[300px]">
                <img
                  src={project.img}
                  alt={project.title}
                  className="absolute inset-0 object-cover w-[300px]
                h-[400px] z-0 rounded-lg"
                />

                {/* Video */}
                <div className="relative flex items-center justify-center h-[500px]">
                  <video
                    loop
                    autoPlay
                    muted
                    playsInline
                    className="shadow-lg z-10"
                    width="150"
                    src={project.video}
                  >
                    Your browser does not support this video.
                  </video>
                </div>
              </div>
            </motion.div>

            {/* Project Details */}
            <div className="z-10 mt-4 px-[24%] flex flex-col items-start">
              <p className="text-2xl lg:text-3xl text-off-white">
                {project.title}
              </p>
            </div>
            <div
              className="absolute text-[18px] text-gray-400 mt-1 ml-11 flex flex-col 
              px-2 py-[2px] bg-transparent border-gray-400 border rounded-full"
            >
              <span className="flex text-center">REACT</span>
            </div>
            <div
              className="absolute text-[18px] text-gray-400 mt-1 ml-[125px]
              px-2 py-[2px] bg-transparent border-gray-400 border rounded-full"
            >
              <span className="flex text-center">THREEJS</span>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ProjectItem;
